import React, { useMemo, useState } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  ResponsiveContainer,
} from 'recharts';
import { plotChartRow, unitModeLabel } from '../../utils/fabHistogramPlot';
import { legendHoverLines } from '../../utils/fabHistogramCatalog';
import { formatTimeAxisTooltip, timeAxisHeight } from '../../utils/histogramTimeAxis';
import HistogramTimeTick from '../common/HistogramTimeTick';

/** Distinct hues — index-based so neighbours are not similar. */
export const FAB_PALETTE = [
  '#1d4ed8',
  '#ea580c',
  '#16a34a',
  '#9333ea',
  '#dc2626',
  '#0891b2',
  '#ca8a04',
  '#db2777',
  '#4f46e5',
  '#65a30d',
  '#0d9488',
  '#c026d3',
  '#b45309',
  '#0369a1',
  '#7c2d12',
];

export function stripDumpSuffix(name) {
  return String(name || '')
    .replace(/\.(xlsx|xls)$/i, '')
    .replace(/\s*[-–—]\s*data\s*dump\s*$/i, '')
    .trim();
}

export function seriesColor(name, index, overrides = {}) {
  if (overrides[name]) return overrides[name];
  const clean = stripDumpSuffix(name);
  if (clean && overrides[clean]) return overrides[clean];
  if (clean && overrides[`${clean} - Data Dump`]) return overrides[`${clean} - Data Dump`];
  const i = Number.isFinite(index) ? index : 0;
  return FAB_PALETTE[((i % FAB_PALETTE.length) + FAB_PALETTE.length) % FAB_PALETTE.length];
}

function fmt(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('en-CA', { maximumFractionDigits: 0 });
}

const SWATCH = {
  width: 14,
  height: 14,
  borderRadius: 3,
  flexShrink: 0,
  display: 'block',
  boxSizing: 'border-box',
};

function LegendHoverBalloon({ lines, anchor }) {
  if (!lines?.length || !anchor) return null;
  const width = 280;
  let left = anchor.right + 10;
  if (left + width > window.innerWidth - 8) {
    left = Math.max(8, anchor.left - width - 10);
  }
  const top = Math.max(8, Math.min(anchor.top, window.innerHeight - 220));
  return (
    <div
      role="tooltip"
      style={{
        position: 'fixed',
        left,
        top,
        width,
        zIndex: 90,
        pointerEvents: 'none',
        background: 'white',
        border: '1px solid #99f6e4',
        borderRadius: 10,
        padding: '10px 12px',
        boxShadow: '0 12px 32px rgba(15,23,42,0.16)',
        maxHeight: 360,
        overflow: 'hidden',
      }}
    >
      {lines.map((line, i) => {
        if (line.kind === 'rule') {
          return <div key={`r-${i}`} style={{ height: 1, background: '#e2e8f0', margin: '8px 0' }} />;
        }
        if (line.kind === 'title') {
          return (
            <div key={`t-${i}`} style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', marginBottom: 6, lineHeight: 1.3 }}>
              {line.text}
            </div>
          );
        }
        if (line.kind === 'name') {
          return (
            <div key={`n-${i}`} style={{ fontSize: 12, fontWeight: 600, color: '#0f766e', marginBottom: 4, lineHeight: 1.35 }}>
              {line.text}
            </div>
          );
        }
        if (line.kind === 'muted') {
          return (
            <div key={`m-${i}`} style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              {line.text}
            </div>
          );
        }
        return (
          <div
            key={`f-${i}`}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 10,
              fontSize: 12,
              lineHeight: '18px',
              color: '#334155',
            }}
          >
            <span style={{ color: '#64748b', flexShrink: 0 }}>{line.label}</span>
            <span style={{ textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis' }}>{line.text}</span>
          </div>
        );
      })}
    </div>
  );
}

function LegendSwatch({ color, onChange, hidePicker }) {
  return (
    <span style={{ position: 'relative', width: 14, height: 14, flexShrink: 0 }}>
      <span style={{ ...SWATCH, background: color, border: '1px solid rgba(15,23,42,0.15)' }} />
      {!hidePicker && (
        <input
          type="color"
          value={color}
          onChange={(e) => onChange?.(e.target.value)}
          title="Change colour"
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0,
            width: 14,
            height: 14,
            padding: 0,
            border: 'none',
            cursor: 'pointer',
          }}
        />
      )}
    </span>
  );
}

function fmtMen(n) {
  if (n == null || !Number.isFinite(Number(n))) return '';
  const v = Math.round(Number(n));
  return v > 0 ? String(v) : '';
}

/** Whole-number stack total sitting on top of the bar. */
function BarTotalLabel(props) {
  const vb = props.viewBox || {};
  const x = Number(props.x ?? vb.x);
  const y = Number(props.y ?? vb.y);
  const width = Number(props.width ?? vb.width);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  const rounded = Math.round(Number(props.value));
  if (!Number.isFinite(rounded) || rounded <= 0) return null;
  return (
    <text
      x={x + (Number.isFinite(width) ? width / 2 : 0)}
      y={y - 4}
      textAnchor="middle"
      fill="#0f172a"
      fontSize={10}
      fontWeight={700}
      pointerEvents="none"
    >
      {rounded.toLocaleString('en-CA', { maximumFractionDigits: 0 })}
    </text>
  );
}

/** White men count, only when the stack segment is tall/wide enough to read. */
function MenInBarLabel(props) {
  const vb = props.viewBox || {};
  const x = Number(props.x ?? vb.x);
  const y = Number(props.y ?? vb.y);
  const width = Number(props.width ?? vb.width);
  const height = Number(props.height ?? vb.height);
  if (!Number.isFinite(x) || !Number.isFinite(y) || height < 14 || width < 12) return null;
  const text = fmtMen(props.value);
  if (!text) return null;
  return (
    <text
      x={x + width / 2}
      y={y + height / 2}
      textAnchor="middle"
      dominantBaseline="central"
      fill="#ffffff"
      fontSize={width < 18 ? 8 : 10}
      fontWeight={700}
      pointerEvents="none"
      style={{ textShadow: '0 0 3px rgba(15,23,42,0.75)' }}
    >
      {text}
    </text>
  );
}

const CUM_LABEL = 'Cumulative hours';

function ZeroSkipTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p) => {
    if (p.dataKey === '_total') return false;
    const v = Math.round(Number(p.value));
    return Number.isFinite(v) && v !== 0;
  });
  if (!rows.length) return null;
  return (
    <div style={{
      background: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      padding: '8px 10px',
      boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
      maxWidth: 320,
    }}>
      <div style={{ fontWeight: 700, fontSize: 12, color: '#0f172a', marginBottom: 6 }}>{formatTimeAxisTooltip(label)}</div>
      {rows.map((p) => {
        const raw = p.dataKey === '_cap' ? 'Shop capacity' : p.dataKey === '_cum' ? CUM_LABEL : p.name;
        const name = raw === 'Shop capacity' || raw === CUM_LABEL ? raw : stripDumpSuffix(raw);
        return (
          <div
            key={p.dataKey}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              fontSize: 12,
              lineHeight: '18px',
              color: p.color || '#334155',
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
            <span style={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{fmt(p.value)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function FabricationHistogramChart({
  chart = [],
  series = [],
  units = 'hours',
  grain = 'week',
  showCapacity = true,
  showSCurve = false,
  showMenLabels = false,
  showBarTotals = true,
  seriesColors = {},
  hideColorPickers = false,
  onColorChange,
  seriesMeta = {},
}) {
  const [spotlight, setSpotlight] = useState(null);
  const [hover, setHover] = useState(null);

  const data = useMemo(
    () => chart.map((row) => plotChartRow(row, series, units)),
    [chart, series, units]
  );

  const unitLabel = unitModeLabel(units);

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', minHeight: 500 }}>
      <div style={{
        width: 210,
        flexShrink: 0,
        maxHeight: 500,
        overflowY: 'auto',
        paddingRight: 4,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 8 }}>
          Legend · {unitLabel}
        </div>
        <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 8, lineHeight: 1.35 }}>
          {spotlight ? `Spotlight ${stripDumpSuffix(spotlight)}` : 'Hover a name for the job · click to spotlight'}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {series.map((name, i) => {
            const color = seriesColor(name, i, seriesColors);
            const label = stripDumpSuffix(name) || name;
            const dim = spotlight && spotlight !== name;
            const openHover = (el) => {
              if (!el) return;
              setHover({ name, rect: el.getBoundingClientRect() });
            };
            return (
              <div
                key={name}
                onMouseEnter={(e) => {
                  if (e.target.closest?.('input[type="color"]')) return;
                  openHover(e.currentTarget);
                }}
                onMouseLeave={() => setHover((prev) => (prev?.name === name ? null : prev))}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  opacity: dim ? 0.35 : 1,
                }}
              >
                <LegendSwatch
                  color={color}
                  hidePicker={hideColorPickers}
                  onChange={(hex) => onColorChange?.(name, hex)}
                />
                <button
                  type="button"
                  onClick={() => setSpotlight((prev) => (prev === name ? null : name))}
                  onFocus={(e) => openHover(e.currentTarget.parentElement)}
                  onBlur={() => setHover((prev) => (prev?.name === name ? null : prev))}
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    textAlign: 'left',
                    fontSize: 12,
                    lineHeight: '16px',
                    color: '#334155',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  {label}
                </button>
              </div>
            );
          })}
          {showCapacity && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <span style={{ ...SWATCH, background: '#0f172a', border: '1px solid rgba(15,23,42,0.15)' }} />
              <span style={{ fontSize: 12, lineHeight: '16px', color: '#334155' }}>Shop capacity</span>
            </div>
          )}
          {showSCurve && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ ...SWATCH, background: '#64748b', border: '1px solid rgba(15,23,42,0.15)' }} />
              <span style={{ fontSize: 12, lineHeight: '16px', color: '#334155' }}>{CUM_LABEL}</span>
            </div>
          )}
        </div>
        {hover && (
          <LegendHoverBalloon
            lines={legendHoverLines(seriesMeta[hover.name] || { label: stripDumpSuffix(hover.name) || hover.name }, hover.name)}
            anchor={hover.rect}
          />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0, height: 500 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 18, right: 4, left: 0, bottom: 8 }} barCategoryGap="3%" barGap={0}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="period"
              interval={0}
              height={timeAxisHeight(grain)}
              tick={<HistogramTimeTick />}
              axisLine={{ stroke: '#94a3b8' }}
              tickLine={{ stroke: '#94a3b8' }}
              padding={{ left: 0, right: 0 }}
            />
            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => fmt(v)} width={36} />
            {showSCurve && (
              <YAxis
                yAxisId="cum"
                orientation="right"
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                tickFormatter={(v) => fmt(v)}
                width={44}
                label={{ value: 'Hours', angle: 90, position: 'insideRight', style: { fill: '#94a3b8', fontSize: 10 } }}
              />
            )}
            <Tooltip content={<ZeroSkipTooltip />} />
            {series.map((name, i) => (
              <Bar
                key={name}
                dataKey={name}
                name={stripDumpSuffix(name) || name}
                stackId="load"
                fill={seriesColor(name, i, seriesColors)}
                fillOpacity={!spotlight || spotlight === name ? 0.92 : 0.18}
                isAnimationActive={false}
              >
                {showMenLabels && (
                  <LabelList
                    dataKey={name}
                    position="center"
                    content={(props) => (
                      <MenInBarLabel
                        {...props}
                        value={data[props.index]?.[`${name}__men`]}
                      />
                    )}
                  />
                )}
                {showBarTotals && i === series.length - 1 && (
                  <LabelList dataKey="_total" position="top" content={BarTotalLabel} />
                )}
              </Bar>
            ))}
            {showCapacity && (
              <Line
                type="stepAfter"
                dataKey="_cap"
                name="Shop capacity"
                stroke="#0f172a"
                strokeWidth={2}
                dot={false}
              />
            )}
            {showSCurve && (
              <Line
                type="monotone"
                dataKey="_cum"
                name={CUM_LABEL}
                stroke="#64748b"
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                yAxisId="cum"
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function slicerValue(opt) {
  if (opt && typeof opt === 'object') return String(opt.value);
  return String(opt);
}

function slicerLabel(opt) {
  if (opt && typeof opt === 'object') return String(opt.label ?? opt.value);
  return String(opt);
}

export function Slicer({ label, options, selected, onChange, footer }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => slicerLabel(o).toLowerCase().includes(q));
  }, [options, search]);
  const active = selected.size;

  const toggle = (val) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onChange(next);
  };

  return (
    <div style={{ position: 'relative', minWidth: 168 }}>
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); setSearch(''); }}
        style={{
          width: '100%',
          padding: '6px 10px',
          fontSize: '0.78rem',
          border: `1px solid ${active ? '#0f766e' : '#d1d5db'}`,
          borderRadius: 6,
          background: active ? '#f0fdfa' : 'white',
          color: '#334155',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 6,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {active ? `${label} (${active})` : label}
        </span>
        <span style={{ opacity: 0.45, fontSize: '0.65rem' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 20 }} onClick={() => setOpen(false)} />
          <div style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 4, width: 220, maxHeight: 260,
            background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, zIndex: 21,
            display: 'flex', flexDirection: 'column',
          }}>
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              style={{ margin: 6, padding: '5px 8px', fontSize: '0.78rem', border: '1px solid #e2e8f0', borderRadius: 4 }}
            />
            <div style={{ display: 'flex', gap: 12, padding: '0 10px 6px' }}>
              <button
                type="button"
                onClick={() => onChange(new Set([...selected, ...filtered.map(slicerValue)]))}
                disabled={!filtered.length}
                style={{ border: 'none', background: 'none', color: filtered.length ? '#0f766e' : '#94a3b8', fontSize: '0.72rem', fontWeight: 700, padding: 0, cursor: filtered.length ? 'pointer' : 'default' }}
              >
                Select all
              </button>
              <button
                type="button"
                onClick={() => onChange(new Set())}
                disabled={!active}
                style={{ border: 'none', background: 'none', color: active ? '#0f766e' : '#94a3b8', fontSize: '0.72rem', fontWeight: 700, padding: 0, cursor: active ? 'pointer' : 'default' }}
              >
                Clear all
              </button>
            </div>
            <div style={{ overflowY: 'auto' }}>
              {filtered.map((opt) => {
                const value = slicerValue(opt);
                return (
                  <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', fontSize: '0.78rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={selected.has(value)} onChange={() => toggle(value)} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{slicerLabel(opt)}</span>
                  </label>
                );
              })}
              {!filtered.length && <div style={{ padding: 10, fontSize: '0.75rem', color: '#94a3b8' }}>No matches</div>}
            </div>
            {footer && (
              <div style={{ borderTop: '1px solid #e2e8f0', padding: '8px 10px' }}>
                {footer}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
